<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
  ChartConfiguration,
} from 'chart.js';
import { generateColors, withAlpha } from '@/utils/color';
import { ChartData } from '@/composables/useSectorStats';

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler);

const props = defineProps<{ stats: ChartData }>();

const canvas = ref<HTMLCanvasElement | null>(null);
let chart: Chart | null = null;

const isDark = ref(false);
let observer: MutationObserver | null = null;

const config = computed<ChartConfiguration<'line'>>(() => {
  const { dates, topSectorsByGrowth, cumulative, growth, highestGrowth } = props.stats;
  const colors = generateColors(5);

  const datasets = topSectorsByGrowth.map((sector, idx) => {
    const isHot = sector === highestGrowth.sector && highestGrowth.value > 0;
    const color = colors[idx];
    return {
      label: `${sector}${isHot ? ' ★' : ''} (周+${growth[sector] ?? 0})`,
      data: cumulative[sector] ?? [],
      borderColor: color,
      backgroundColor: isHot ? withAlpha(color, 0.12) : 'transparent',
      borderWidth: isHot ? 3 : 2,
      fill: isHot,
      tension: 0.25,
      pointRadius: isHot ? 3 : 0,
      pointHoverRadius: 6,
      pointBackgroundColor: color,
      pointBorderColor: isDark.value ? 'rgb(17 24 39)' : '#fff',
      pointBorderWidth: 2,
      order: isHot ? 0 : idx + 1,
    };
  });

  return {
    type: 'line',
    data: { labels: dates, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            color: isDark.value ? '#e2e8f0' : '#334155',
            padding: 16,
            usePointStyle: true,
            font: { size: 12 },
          },
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: 'rgba(255,255,255,0.15)',
          borderWidth: 1,
          padding: 10,
        },
      },
      scales: {
        x: {
          title: { display: true, text: '日期', color: isDark.value ? '#94a3b8' : '#64748b' },
          ticks: { color: isDark.value ? '#cbd5e1' : '#475569' },
          grid: { color: isDark.value ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.05)' },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: '累计得分', color: isDark.value ? '#94a3b8' : '#64748b' },
          ticks: { color: isDark.value ? '#cbd5e1' : '#475569' },
          grid: { color: isDark.value ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.05)' },
        },
      },
      elements: {
        line: { borderCapStyle: 'round' as const, borderJoinStyle: 'round' as const },
      },
    },
  };
});

function render() {
  if (!canvas.value) return;
  if (chart) {
    chart.data = config.value.data;
    chart.options = config.value.options as any;
    chart.update('none');
    return;
  }
  chart = new Chart(canvas.value, config.value);
}

onMounted(() => {
  isDark.value = document.documentElement.classList.contains('dark');
  render();
  observer = new MutationObserver(() => {
    isDark.value = document.documentElement.classList.contains('dark');
    render();
  });
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
});

onBeforeUnmount(() => {
  chart?.destroy();
  chart = null;
  observer?.disconnect();
});

watch(config, render, { deep: true });
</script>

<template>
  <section class="card">
    <div class="mb-3 flex flex-wrap items-end justify-between gap-2">
      <div>
        <h2 class="flex items-center gap-2 text-base font-semibold">
          <span class="h-1.5 w-1.5 rounded-full" style="background: rgb(var(--color-primary));"></span>
          板块累计得分趋势
        </h2>
        <p class="mt-1 text-xs text-ink-muted">
          近 2 个月 Top 5 板块
          <span v-if="stats.highestGrowth.sector && stats.highestGrowth.value > 0" class="ml-2 text-warning">
            ★ 近一周最强势：{{ stats.highestGrowth.sector }} (+{{ stats.highestGrowth.value }} 分)
          </span>
        </p>
      </div>
    </div>

    <div v-if="stats.dates.length === 0" class="flex h-[400px] items-center justify-center text-ink-subtle">
      暂无数据，先添加第一条记录
    </div>
    <div v-else class="h-[420px]">
      <canvas ref="canvas"></canvas>
    </div>
  </section>
</template>
